// @flow

import * as React from 'react';
import {defineMessages, useIntl} from 'react-intl';
import Button from 'sail/Button';
import Group from 'sail/Group';

import {useStripeClui} from 'docs/components/contrib/StripeClui/helpers/react/provider';
import SubscriptionLifecycleSvgDiagram from './SubscriptionLifecycleSvgDiagram';

import './SubscriptionLifecycleDiagram.scss';

const messages = defineMessages({
  startVisualization: {
    id: 'docsApp.subscriptionLifecycleDiagram.startVisualization',
    description:
      'Label on the action to start the visualization of a subscription lifecycle via the Stripe Shell',
    defaultMessage: 'Visualize with Stripe Shell',
  },
  resetVisualization: {
    id: 'docsApp.subscriptionLifecycleDiagram.resetVisualization',
    description:
      'Label on the action to reset the visualization of a subscription lifecycle via the Stripe Shell',
    defaultMessage: 'Reset visualization',
  },
});

type DiagramPhase =
  | 'initial'
  | 'interactive'
  | 'successLoading'
  | 'successComplete'
  | 'failLoading'
  | 'failComplete';

export const SubscriptionLifecycleDiagram = () => {
  const [phase, setPhase] = React.useState<DiagramPhase>('initial');
  const diagramRef = React.useRef<HTMLElement | null>(null);
  const {formatMessage} = useIntl();

  const {
    state: {panes},
    open,
    addPane,
    focusPane,
    invokeCommand,
    reset,
    lockPanes,
    unlockPanes,
  } = useStripeClui();

  React.useEffect(() => {
    if (diagramRef.current && phase === 'interactive') {
      // Need to wait until element is present before scrolling into view.
      setTimeout(() => {
        if (diagramRef.current) {
          diagramRef.current.scrollIntoView({behavior: 'smooth'});
        }
      }, 0);
    }
  }, [phase]);

  const handleSuccessComplete = React.useCallback(() => {
    setPhase('successComplete');
    unlockPanes();
  }, [setPhase, unlockPanes]);

  const handleFailComplete = React.useCallback(() => {
    setPhase('failComplete');
    unlockPanes();
  }, [setPhase, unlockPanes]);

  React.useEffect(() => {
    const triggerPaneUnlocked = panes?.[1]?.allowInput ?? false;

    if (phase === 'successLoading' && triggerPaneUnlocked) {
      handleSuccessComplete();
    }

    if (phase === 'failLoading' && triggerPaneUnlocked) {
      handleFailComplete();
    }
  }, [phase, panes, handleSuccessComplete, handleFailComplete]);

  const handleInitialize = () => {
    open();
    addPane();
    invokeCommand({command: 'stripe listen'});
    lockPanes();

    setPhase('interactive');
  };

  const handleSuccessClick = () => {
    focusPane(0);
    invokeCommand({command: 'stripe trigger subscription.payment_succeeded'});

    setPhase('successLoading');
  };

  const handleFailClick = () => {
    focusPane(0);
    invokeCommand({command: 'stripe trigger subscription.payment_failed'});

    setPhase('failLoading');
  };

  const handleReset = () => {
    reset();
    handleInitialize();
  };

  let content = null;

  switch (phase) {
    case 'initial':
      content = (
        <Group
          direction="vertical"
          alignItems="flex-start"
          justifyContent="flex-start"
        >
          <Button
            icon="api"
            label={formatMessage(messages.startVisualization)}
            size="jumbo"
            color="blue"
            className="VisualizeButton"
            onClick={handleInitialize}
          />
          <SubscriptionLifecycleSvgDiagram className="initial" />
        </Group>
      );
      break;
    case 'interactive':
      content = (
        <div ref={diagramRef}>
          <SubscriptionLifecycleSvgDiagram
            className="interactive"
            onSuccessClick={handleSuccessClick}
            onFailClick={handleFailClick}
          />
        </div>
      );

      break;
    case 'successLoading':
    case 'failLoading':
      content = <SubscriptionLifecycleSvgDiagram className={phase} />;
      break;
    case 'successComplete':
    case 'failComplete':
      content = (
        <Group
          direction="vertical"
          alignItems="flex-start"
          justifyContent="flex-start"
        >
          <Button
            icon="api"
            label={formatMessage(messages.resetVisualization)}
            size="jumbo"
            color="blue"
            onClick={handleReset}
            className="VisualizeButton"
          />
          <SubscriptionLifecycleSvgDiagram className={phase} />
        </Group>
      );
      break;
  }

  return <div className="SubscriptionLifecycleDiagram">{content}</div>;
};
